import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TablePagination,
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import SearchBar from "../../components/SearchBar";
import { useGetSubscription, useUpdateSubscription, useGetUserPurchases } from "../../api/hooks/useSubscription";
import { showError, showSuccess } from "../../helpers/toast";
import type { Subscription, SubscriptionUserPurchase } from "../../api/types/subscription.types";

const ViewSubscription = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const subscriptionId = location?.state?.subscriptionId;

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(0); // Reset to first page on search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch subscription data
    const { data: subscriptionData, isLoading: isLoadingSubscription, error: subscriptionError } = useGetSubscription(
        subscriptionId || '',
        { enabled: !!subscriptionId }
    );

    const subscription: Subscription | null = subscriptionData?.data || null;

    // Fetch user purchases
    const { data: userPurchasesData, isLoading: isLoadingUserPurchases } = useGetUserPurchases(
        subscriptionId || '',
        {
            search: debouncedSearchTerm,
            page: page + 1, // API uses 1-based indexing
            limit: rowsPerPage,
        },
        { enabled: !!subscriptionId }
    );

    const userPurchases = userPurchasesData?.data?.list || [];
    const totalCount = userPurchasesData?.data?.totalCount || 0;

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Update subscription status mutation
    const { mutate: updateSubscriptionStatus, isPending: isUpdatingStatus } = useUpdateSubscription({
        onSuccess: (response) => {
            showSuccess(response.message || "Subscription status updated successfully");
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to update subscription status");
        },
    });

    useEffect(() => {
        if (subscriptionError) {
            showError((subscriptionError as any)?.response?.data?.message || "Failed to load subscription details");
        }
    }, [subscriptionError]);

    useEffect(() => {
        if (!subscriptionId && !isLoadingSubscription) {
            showError("No subscription selected. Redirecting to subscription list.");
            navigate("/subscription");
        }
    }, [subscriptionId, isLoadingSubscription, navigate]);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    const formatPrice = (price: number) => {
        if (price === 0) return "Free";
        return `$${price.toFixed(2)}`;
    };

    const formatType = (type: number) => {
        switch (type) {
            case 1:
                return "Free";
            case 2:
                return "Premium";
            default:
                return "N/A";
        }
    };

    const formatValidity = (validity: number) => {
        if (!validity) return "N/A";
        if (validity === 365) return "1 Year";
        if (validity === 30) return "1 Month";
        if (validity < 30) return `${validity} Days`;
        if (validity < 365) {
            const months = Math.floor(validity / 30);
            return `${months} Month${months > 1 ? 's' : ''}`;
        }
        const years = Math.floor(validity / 365);
        return `${years} Year${years > 1 ? 's' : ''}`;
    };

    const formatBenefits = (benefits: string) => {
        if (!benefits) return [];
        // Split by comma or newline
        return benefits.split(/[,\n]/).map(b => b.trim()).filter(b => b.length > 0);
    };

    const handleStatusToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (subscription) {
            updateSubscriptionStatus({
                id: subscription._id,
                data: {
                    price: subscription.price,
                    benefits: subscription.benefits,
                    status: event.target.checked,
                },
            });
        }
    };

    if (isLoadingSubscription) {
        return (
            <>
                <div className="main_title">
                    <h1>Subscription Details</h1>
                    <p>
                        <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                        <button onClick={() => navigate("/subscription")}>
                            Manage Subscriptions
                        </button>{" "}
                        - View Subscription
                    </p>
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    if (!subscription) {
        return (
            <>
                <div className="main_title">
                    <h1>Subscription Details</h1>
                    <p>
                        <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                        <button onClick={() => navigate("/subscription")}>
                            Manage Subscriptions
                        </button>{" "}
                        - View Subscription
                    </p>
                </div>
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <p>Subscription not found.</p>
                </Box>
            </>
        );
    }

    const benefits = formatBenefits(subscription.benefits || "");

    return (
        <>
            <div className="main_title">
                <h1>Subscription Details</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/subscription")}>
                        Manage Subscriptions
                    </button>{" "}
                    - View Subscription
                </p>
            </div>

            <div className="cards">
                <div className="view_page">
                    <div className="table_header">
                        <h2>Subscription Details</h2>
                    </div>

                    <div className="gap_p">
                        <div className="w_33">
                            <h6>Subscription Type</h6>
                            <p>{formatType(subscription.type)}</p>
                        </div>

                        <div className="w_33">
                            <h6>Subscription Name</h6>
                            <p>{subscription.name || "N/A"}</p>
                        </div>

                        <div className="w_33">
                            <h6>Validity</h6>
                            <p>{formatValidity(subscription.validity)}</p>
                        </div>

                        <div className="w_33">
                            <h6>Price</h6>
                            <p>{formatPrice(subscription.price)}</p>
                        </div>

                        <div className="w_33">
                            <h6>Status</h6>
                            <p>
                                <SwitchToggle
                                    checked={subscription.status ?? true}
                                    onChange={handleStatusToggle}
                                    disabled={isUpdatingStatus}
                                />
                            </p>
                        </div>

                        <div className="w_33">
                            <h6>Created Date</h6>
                            <p>{formatDate(subscription.createdAt)}</p>
                        </div>

                        <div className="w_100">
                            <h6>Benefits</h6>
                            {benefits.length > 0 ? (
                                <ul className="inline_tags">
                                    {benefits.map((benefit, i) => (
                                        <li key={i}>{benefit}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p>N/A</p>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ margin: "20px 0" }} />

                <div className="title_inner">
                    <h2>Users</h2>
                </div>

                <div className="table_header">
                    <div className="left_s">
                        <SearchBar
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            onCross={() => setSearchTerm("")}
                            searchTerm={searchTerm}
                            setDebouncedSearchTerm={setSearchTerm}
                            placeholder="Search users..."
                        />
                    </div>
                </div>

                <TableContainer className="table_container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr. No.</TableCell>
                                <TableCell>User Details</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoadingUserPurchases ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : userPurchases.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                        No users found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                userPurchases.map((purchase: SubscriptionUserPurchase, index: number) => {
                                    const user = purchase.user?.[0];
                                    return (
                                        <TableRow key={purchase._id} hover>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                <div className="user_block">
                                                    <figure>
                                                        <img
                                                            src="/static/images/user_placeholder.png"
                                                            alt="User"
                                                        />
                                                    </figure>
                                                    <p>
                                                        <b>{user?.fullName || "N/A"}</b>
                                                        <br />
                                                        {user?.email || "N/A"}
                                                        <br />
                                                        {user?.countryCode || ""} {user?.phone || ""}
                                                    </p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`status_btn ${purchase.status === 5 ? "status_active" : "status_blocked"}`}>
                                                    {purchase.status === 5 ? "Active" : "Inactive"}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </div>
        </>
    );
};

export default ViewSubscription;
